import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { BusinessChatPanel } from '@/components/chat/BusinessChatPanel';

interface ModernAIAssistantSidebarProps {
  businessId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateComplete?: () => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 384; // w-96
const STORAGE_KEY = 'chat-sidebar-width';

export const ModernAIAssistantSidebar: React.FC<ModernAIAssistantSidebarProps> = ({
  businessId,
  isOpen,
  onOpenChange,
  onUpdateComplete,
}) => {
  const [businessName, setBusinessName] = useState<string>('');
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    // Load saved width from localStorage or use default
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
    }
    return DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Fetch business name when component mounts or businessId changes
  useEffect(() => {
    const fetchBusinessName = async () => {
      if (businessId) {
        const { data: business } = await supabase
          .from('businesses')
          .select('business_name')
          .eq('id', businessId)
          .single();

        if (business) {
          setBusinessName(business.business_name || 'Unknown Business');
        }
      } else {
        setBusinessName('General Chat');
      }
    };

    fetchBusinessName();
  }, [businessId]);

  // Handle resize drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        // Save width to localStorage
        localStorage.setItem(STORAGE_KEY, sidebarWidth.toString());
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, sidebarWidth]);

  return (
    <div
      ref={sidebarRef}
      style={{
        width: isOpen ? `${sidebarWidth}px` : '0px',
        minWidth: isOpen ? `${sidebarWidth}px` : '0px',
      }}
      className={`relative bg-background border-l border-border flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
        !isOpen ? 'border-l-0' : ''
      }`}
    >
      {/* Resize Handle - Only show when open */}
      {isOpen && (
        <div
          onMouseDown={() => setIsResizing(true)}
          className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/10 active:bg-primary/20 transition-colors group z-10"
        >
          {/* Visual indicator */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-border/60 group-hover:bg-primary/50 group-active:bg-primary rounded-r transition-all" />
        </div>
      )}

      {/* Header - Clean Layout (with left padding to avoid resize handle) */}
      <div className="flex items-center justify-between h-[52px] pl-6 pr-2 border-b border-[#f0f0f0] bg-background">
        {/* Left Column - Business Name */}
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium truncate" title={businessName}>
            {businessName}
          </span>
        </div>

        {/* Right Side - Close Icon */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          className="h-7 w-7 p-0 flex-shrink-0 hover:bg-[#f0f0f0]"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Business Chat Panel */}
      <BusinessChatPanel
        businessId={businessId}
        className="flex-1"
        onBusinessDataUpdate={onUpdateComplete}
      />
    </div>
  );
};